import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DeepLinkServices } from './DeepLinkServices';
import { fromIstInput, toIstInput } from './shortLinkDisplay';

const ShortLinkDialog = ({ link, mode, onClose, onSaved }) => {
  const [error, setError] = useState('');
  const { control, handleSubmit, formState: { isSubmitting } } = useForm({
    defaultValues: {
      full_url: link?.full_url || '', title: link?.title || '', custom_alias: '',
      expires_at: toIstInput(link?.expires_at), is_active: link?.is_active !== false,
    },
  });
  const generateOnly = mode === 'generate';
  const save = async (values) => {
    setError('');
    try {
      let response;
      if (generateOnly) {
        response = await DeepLinkServices.generateShortUrl(link._id, values.custom_alias ? { custom_alias: values.custom_alias } : {});
      } else {
        const payload = { title: values.title, full_url: values.full_url, expires_at: fromIstInput(values.expires_at), is_active: values.is_active };
        if (mode === 'create') {
          if (values.custom_alias) payload.custom_alias = values.custom_alias;
          response = await DeepLinkServices.storeDeepUrl(values.full_url, payload);
        } else {
          response = await DeepLinkServices.updateDeepUrl(link._id, payload);
        }
      }
      if (Number(response?.success) !== 1) throw new Error(response?.message || 'Could not save this link.');
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not save this link.');
    }
  };
  return (
    <Dialog open onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{generateOnly ? 'Generate Short URL' : mode === 'create' ? 'Create Short Link' : 'Edit Link'}</DialogTitle>
      <form onSubmit={handleSubmit(save)}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!generateOnly && <>
            <Controller name="title" control={control} rules={{ maxLength: { value: 200, message: 'Use at most 200 characters.' } }} render={({ field, fieldState }) => <TextField {...field} label="Title" fullWidth error={!!fieldState.error} helperText={fieldState.error?.message} sx={{ mb: 2 }} />} />
            <Controller name="full_url" control={control} rules={{ required: 'Destination URL is required.', validate: (value) => {
              try { return new URL(value).protocol === 'https:' || 'Use an HTTPS URL.'; } catch { return 'Enter a valid HTTPS URL.'; }
            } }} render={({ field, fieldState }) => <TextField {...field} label="Destination URL" multiline minRows={3} fullWidth required error={!!fieldState.error} helperText={fieldState.error?.message || 'Only approved CuDel domains are supported. Query parameters are preserved.'} sx={{ mb: 2 }} />} />
          </>}
          {mode !== 'edit' && <Controller name="custom_alias" control={control} render={({ field, fieldState }) => <TextField {...field} label="Custom alias (optional)" fullWidth error={!!fieldState.error} helperText={fieldState.error?.message || 'Leave blank for a random alias. Spaces and special characters are encoded in the short URL. Aliases cannot be changed or reused.'} sx={{ mb: 2 }} />} />}
          {!generateOnly && <>
            <Controller name="expires_at" control={control} render={({ field }) => <TextField {...field} label="Expiry (IST)" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} helperText="Leave blank for no expiry. Time is in India Standard Time." sx={{ mb: 2 }} />} />
            <Controller name="is_active" control={control} render={({ field }) => <FormControlLabel control={<Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />} label="Active" />} />
          </>}
          {mode === 'edit' && <Alert severity="info">The short alias stays fixed when you edit the destination.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ShortLinkDialog;
